import { useRef, useState, type ReactNode } from 'react';

const COMMIT_PX = 96;
const MAX_PX = 140;
const SLOP_PX = 8;

interface Gesture { x: number; y: number; mode: 'pending' | 'swiping' | 'vertical' }

/**
 * A list row that a finger can swipe left to delete. Touch only: a mouse or keyboard user keeps the row's own delete button.
 * Vertical movement is left to the browser, so the list still scrolls.
 */
export function SwipeToDelete({ onDelete, children }: { onDelete: () => void; children: ReactNode }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef<Gesture | null>(null);
  const swiped = useRef(false);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== 'touch') return;
    gesture.current = { x: e.clientX, y: e.clientY, mode: 'pending' };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const g = gesture.current;
    if (!g || g.mode === 'vertical') return;
    const moveX = e.clientX - g.x;
    const moveY = e.clientY - g.y;
    if (g.mode === 'pending') {
      if (Math.abs(moveY) > SLOP_PX && Math.abs(moveY) > Math.abs(moveX)) { g.mode = 'vertical'; return; }
      if (Math.abs(moveX) <= SLOP_PX || Math.abs(moveX) <= Math.abs(moveY)) return;
      g.mode = 'swiping';
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    setDx(Math.max(-MAX_PX, Math.min(0, moveX)));
  }

  function finish(commit: boolean) {
    const wasSwiping = gesture.current?.mode === 'swiping';
    gesture.current = null;
    if (!wasSwiping) return;
    setDragging(false);
    setDx(0);
    // The finger lifting after a swipe would otherwise count as a tap on the row's link.
    swiped.current = true;
    setTimeout(() => { swiped.current = false; }, 0);
    if (commit) onDelete();
  }

  return (
    <div className="sx-swipe">
      <div className="sx-swipe-bg" aria-hidden="true">Delete</div>
      <div
        className={`sx-swipe-fg${dragging ? ' sx-swipe-drag' : ''}`}
        style={{ transform: dx ? `translateX(${dx}px)` : undefined }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => finish(dx <= -COMMIT_PX)}
        onPointerCancel={() => finish(false)}
        onClickCapture={e => { if (swiped.current) { e.preventDefault(); e.stopPropagation(); } }}
      >
        {children}
      </div>
    </div>
  );
}
